import React from 'react';
import { Modal, Form, Input, DatePicker, Alert, App, Select } from 'antd';
import dayjs from 'dayjs';
import { useCreateQuickProject } from '../../hooks/useProject';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { Project } from './ProjectSelector';

const { Option } = Select;

interface QuickProjectCreationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (project: Project) => void;
  defaultCustomerId?: string;
  defaultCustomerName?: string;
}

export const QuickProjectCreationModal: React.FC<QuickProjectCreationModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  defaultCustomerId,
  defaultCustomerName
}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const createProjectMutation = useCreateQuickProject();
  const { data: customers, isLoading: isLoadingCustomers } = useActiveCustomers();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Get customer name from selected customer
      const selectedCustomer = customers?.find(c => c.id === values.customer_id);
      const customerName = selectedCustomer?.customer_name || defaultCustomerName || '';
      
      if (!customerName) {
        message.error('Please select a customer');
        return;
      }
      
      const projectData = {
        project_name: values.project_name,
        customer_id: values.customer_id || defaultCustomerId,
        customer_name: customerName,
        start_date: values.start_date.format('YYYY-MM-DD'),
        department_id: values.department_id,
      };

      console.log('[QuickProjectCreationModal] Creating project with data:', projectData);

      const project = await createProjectMutation.mutateAsync(projectData);
      message.success('Project created successfully!');
      onSuccess(project);
      form.resetFields();
    } catch (error: any) {
      console.error('[QuickProjectCreationModal] Error creating project:', error);
      if (error.errorFields) {
        // Form validation error, don't show message
        return;
      }
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create project';
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Create New Project"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={createProjectMutation.isPending}
      width={500}
      okText="Create Project"
      cancelText="Cancel"
    >
      <Form 
        form={form} 
        layout="vertical"
        initialValues={{
          customer_id: defaultCustomerId,
          start_date: dayjs()
        }}
      >
        <Form.Item
          name="project_name"
          label="Project Name"
          rules={[
            { required: true, message: 'Please enter project name' },
            { min: 3, message: 'Project name must be at least 3 characters' }
          ]}
        >
          <Input 
            placeholder="Enter project name" 
            autoFocus
          />
        </Form.Item>
        
        <Form.Item
          name="customer_id"
          label="Customer Name"
          rules={[{ required: true, message: 'Please select customer' }]}
        >
          <Select
            showSearch
            placeholder="Select customer"
            loading={isLoadingCustomers}
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {customers?.map((customer) => (
              <Option key={customer.id} value={customer.id}>
                {customer.customer_name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item
          name="start_date"
          label="Start Date"
          rules={[{ required: true, message: 'Please select start date' }]}
        >
          <DatePicker 
            style={{ width: '100%' }} 
            format="YYYY-MM-DD"
          />
        </Form.Item>

        <Form.Item
          name="department_id"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect />
        </Form.Item>
        
        <Alert
          message="Quick Create"
          description="This creates a basic project with essential information. You can add more details (budget, team, milestones, etc.) later from the Projects page."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
  );
};
